'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ChatWindow from '@/components/chat/ChatWindow';
// PHASE-2.03 START
// PHASE-2.03 END
import { useChat } from '@/hooks/useChat';
import { useClient } from '@/context/ClientContext';
// PHASE-2.03 START
import ConversationHistoryPanel from '@/components/chat-history/ConversationHistoryPanel';
// PHASE-2.03 END
import {
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/Button';
import { cn, formatTimeAgo } from '@/lib/utils';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { buildHandoffCleanupPath } from '@/lib/handoff';
import { ClientContextSwitcher } from '@/components/client/ClientContextSwitcher';
import type { Client } from '@/types';

type TimestampLike = {
  toDate: () => Date;
};

type ConversationTimestamps = {
  updatedAt?: unknown;
  createdAt?: unknown;
};

function isTimestampLike(value: unknown): value is TimestampLike {
  if (!value || typeof value !== 'object') return false;
  return typeof (value as { toDate?: unknown }).toDate === 'function';
}

function toConversationDate(value: unknown): Date | null {
  if (!value) return null;
  if (isTimestampLike(value)) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'number' || typeof value === 'string') return new Date(value);
  return null;
}

export default function Home() {
  const {
    currentConversation,
    messages,
    loading,
    clearCurrentChat,
    // PHASE-01 START
    createNewConversation,
    loadConversation,
    setClientForConversation,
    // PHASE-01 END
  } = useChat();
  const { clients, activeClient, fetchClientById, setActiveClient } = useClient();
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [sidebarRail, setSidebarRail] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem('chat.sidebar.rail') === '1'; } catch { return false; }
  });
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  // PHASE-01 START
  const clientIdFromQuery = searchParams.get('client');
  const [appliedClientId, setAppliedClientId] = useState<string | null>(null);
  const [bindingClient, setBindingClient] = useState(false);
  // PHASE-01 END
  const artifactIdFromQuery = searchParams.get('artifact');
  const handoffActive = searchParams.get('handoff') === '1';
  const [showHeaderClientSwitcher, setShowHeaderClientSwitcher] = useState(false);
  const headerClientSelectorRef = useRef<HTMLDivElement>(null);
  const headerClientSelectionSeqRef = useRef(0);
  const headerClientId = currentConversation
    ? currentConversation.clientId || null
    : activeClient?.id || null;
  const headerActiveClient = useMemo<Client | null>(() => {
    if (!headerClientId) return null;
    if (activeClient?.id === headerClientId) return activeClient;
    return clients.find((client) => client.id === headerClientId) || null;
  }, [activeClient, clients, headerClientId]);

  const handleHandoffConsumed = useCallback(() => {
    router.replace(buildHandoffCleanupPath(pathname, searchParams));
  }, [pathname, router, searchParams]);

  useEffect(() => {
    const readRail = () => {
      try { setSidebarRail(localStorage.getItem('chat.sidebar.rail') === '1'); } catch {}
    };
    const handleSidebarRailChanged: EventListener = () => readRail();
    window.addEventListener('storage', readRail);
    window.addEventListener('sidebar-rail-changed', handleSidebarRailChanged);
    readRail();
    return () => {
      window.removeEventListener('storage', readRail);
      window.removeEventListener('sidebar-rail-changed', handleSidebarRailChanged);
    };
  }, []);

  // PHASE-01 START
  useEffect(() => {
    if (!clientIdFromQuery) {
      if (appliedClientId !== null) {
        setAppliedClientId(null);
      }
      return;
    }

    const activeConversationId = currentConversation?.id;
    const activeConversationClientId = currentConversation?.clientId;

    if (bindingClient || appliedClientId === clientIdFromQuery) {
      if (activeConversationClientId === clientIdFromQuery) {
        return;
      }
      if (bindingClient) {
        return;
      }
    }

    let cancelled = false;

    const bindClientConversation = async () => {
      setBindingClient(true);
      try {
        let clientRecord = clients.find((client) => client.id === clientIdFromQuery);
        if (!clientRecord) {
          clientRecord = await fetchClientById(clientIdFromQuery);
        }
        if (!clientRecord || cancelled) {
          return;
        }

        setActiveClient(clientRecord);

        const conversationMatches = activeConversationClientId === clientIdFromQuery;

        if (!activeConversationId || !conversationMatches) {
          if (activeConversationId && activeConversationClientId && activeConversationClientId !== clientIdFromQuery) {
            const title = clientRecord.name ? `${clientRecord.name} – Chat` : 'Client conversation';
            const conversationId = await createNewConversation(title, clientIdFromQuery);
            if (!cancelled) {
              await loadConversation(conversationId);
            }
          } else if (!activeConversationId) {
            const title = clientRecord.name ? `${clientRecord.name} – Chat` : 'Client conversation';
            const conversationId = await createNewConversation(title, clientIdFromQuery);
            if (!cancelled) {
              await loadConversation(conversationId);
            }
          } else if (!activeConversationClientId) {
            await setClientForConversation(activeConversationId, clientIdFromQuery);
          }
        }

        if (!cancelled) {
          setAppliedClientId(clientIdFromQuery);
        }
      } catch (err) {
        console.error('Failed to bind conversation to client from query', err);
      } finally {
        setBindingClient(false);
      }
    };

    bindClientConversation();

    return () => {
      cancelled = true;
    };
  }, [
    clientIdFromQuery,
    clients,
    fetchClientById,
    setActiveClient,
    currentConversation?.id,
    currentConversation?.clientId,
    appliedClientId,
    bindingClient,
    createNewConversation,
    loadConversation,
    setClientForConversation
  ]);
  // PHASE-01 END

  useEffect(() => {
    if (!headerClientId || headerActiveClient) return;
    let cancelled = false;

    const hydrateHeaderClient = async () => {
      try {
        const clientRecord = await fetchClientById(headerClientId);
        if (!cancelled && clientRecord?.id === headerClientId) {
          setActiveClient(clientRecord);
        }
      } catch (err) {
        console.error('Failed to load header client context', err);
      }
    };

    void hydrateHeaderClient();

    return () => {
      cancelled = true;
    };
  }, [fetchClientById, headerActiveClient, headerClientId, setActiveClient]);

  const handleHeaderClientSelect = useCallback(async (clientId: string | null) => {
    const selectionSeq = headerClientSelectionSeqRef.current + 1;
    headerClientSelectionSeqRef.current = selectionSeq;
    const isLatestSelection = () => headerClientSelectionSeqRef.current === selectionSeq;
    setShowHeaderClientSwitcher(false);
    try {
      if (!clientId) {
        setActiveClient(null);
        if (currentConversation?.id) {
          if (currentConversation.clientId) {
            await setClientForConversation(currentConversation.id, '');
          }
        } else {
          const id = await createNewConversation('New Conversation');
          if (isLatestSelection()) {
            await loadConversation(id);
          }
        }
        return;
      }

      const chosen = (clients || []).find((client) => client.id === clientId) || null;
      setActiveClient(chosen);
      if (currentConversation?.id) {
        if (currentConversation.clientId !== clientId) {
          await setClientForConversation(currentConversation.id, clientId);
        }
      } else {
        const title = chosen?.name ? `${chosen.name} – Chat` : 'Client conversation';
        const id = await createNewConversation(title, clientId);
        if (isLatestSelection()) {
          await loadConversation(id);
        }
      }
    } catch (e) {
      console.error('Failed to apply client selection', e);
    }
  }, [
    clients,
    currentConversation,
    createNewConversation,
    loadConversation,
    setActiveClient,
    setClientForConversation,
  ]);

  useEffect(() => {
    if (!showHeaderClientSwitcher) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (headerClientSelectorRef.current && !headerClientSelectorRef.current.contains(event.target as Node)) {
        setShowHeaderClientSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [showHeaderClientSwitcher]);

  // Hide sidebar when user sends a message (auto-collapse for better reading experience)
  useEffect(() => {
    if (messages && messages.length > 0) {
      // Get the last message
      const lastMessage = messages[messages.length - 1];
      // If the last message is from the user, hide the sidebar to give more space for AI response
      if (lastMessage?.role === 'user') {
        setSidebarVisible(false);
      }
    }
  }, [messages]);

  return (
    <>
      <div id="main" className="flex h-full w-full overflow-hidden bg-background text-foreground">
        {/* Conversation History Sidebar */}
        {sidebarVisible && (
          <aside
            className={cn(
              "hidden md:flex h-full flex-shrink-0 overflow-hidden bg-background/95 backdrop-blur-2xl border-r border-border/50 transition-all duration-300 ease-in-out",
              sidebarRail ? "w-16 md:hover:w-64" : "w-80"
            )}
            title={sidebarRail ? "Hover to peek, click to expand in header" : undefined}
          >
            <ConversationHistoryPanel />
          </aside>
        )}

        {/* Chat Area */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
          {/* Header with breadcrumbs and controls */}
          <header className="relative z-50 flex-shrink-0 overflow-visible border-b border-border/50 bg-background/95 px-4 py-2 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1 overflow-hidden">
                <Breadcrumb
                  items={[
                    { label: 'Chat', href: '/' },
                    ...(headerActiveClient?.name ? [{ label: headerActiveClient.name }] : []),
                    ...(currentConversation?.title ? [{ label: currentConversation.title }] : []),
                  ]}
                />
              </div>
              <div className="flex shrink-0 items-center gap-2 md:gap-3">
                {/* Sidebar Toggle (Mobile) */}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowSidebar(true)}
                  className={cn(
                    "md:hidden text-muted-foreground hover:text-foreground transition-all duration-200"
                  )}
                  aria-label="Open conversations"
                >
                  <Bars3Icon className="h-5 w-5" />
                </Button>

                {/* Client selector */}
                <div ref={headerClientSelectorRef} className="flex items-center">
                  <div className="w-[min(52vw,220px)] md:hidden">
                    <ClientContextSwitcher
                      clients={clients || []}
                      activeClient={headerActiveClient}
                      isOpen={showHeaderClientSwitcher}
                      onToggle={() => setShowHeaderClientSwitcher((value) => !value)}
                      onClose={() => setShowHeaderClientSwitcher(false)}
                      onClientSelect={handleHeaderClientSelect}
                      label="Chat context"
                      align="right"
                      compact
                      presentation="sheet"
                    />
                  </div>
                  <div className="hidden w-[320px] md:block">
                    <ClientContextSwitcher
                      clients={clients || []}
                      activeClient={headerActiveClient}
                      isOpen={showHeaderClientSwitcher}
                      onToggle={() => setShowHeaderClientSwitcher((value) => !value)}
                      onClose={() => setShowHeaderClientSwitcher(false)}
                      onClientSelect={handleHeaderClientSelect}
                      label="Chat context"
                      align="right"
                      compact={false}
                    />
                  </div>
                </div>

                {/* Timestamp */}
                <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground">
                  <ClockIcon className="h-4 w-4" />
                  <span>{(() => {
                    try {
                      const conversation = currentConversation as ConversationTimestamps | null;
                      const date = toConversationDate(conversation?.updatedAt ?? conversation?.createdAt);
                      if (!date || isNaN(date.getTime())) return '';
                      return formatTimeAgo(date);
                    } catch { return ''; }
                  })()}</span>
                </div>
              </div>
            </div>
          </header>

          {/* Chat Content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="h-full">
              <ChatWindow
                messages={messages}
                loading={loading}
                renderMode="article"
                fullWidth
                handoffArtifactId={artifactIdFromQuery || undefined}
                handoffActive={handoffActive}
                onHandoffConsumed={handleHandoffConsumed}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile New Chat FAB */}
      <div className="fixed bottom-20 right-4 z-40 md:hidden">
        <Button
          size="lg"
          variant="glow"
          className="rounded-full shadow-lg"
          onClick={clearCurrentChat}
          aria-label="Start new chat"
        >
          <PlusIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showSidebar && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setShowSidebar(false)}
          />

          {/* Sidebar */}
          <div className="relative w-full max-w-sm h-full">
            <div className="h-full bg-background border-r border-border shadow-2xl animate-slide-in overflow-hidden flex flex-col">
              {/* Sidebar Header */}
              <div className="flex items-center justify-between p-4 border-b border-border/50">
                <h2 className="font-semibold text-lg">Conversations</h2>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setShowSidebar(false)}
                  aria-label="Close sidebar"
                >
                  <XMarkIcon className="h-5 w-5" />
                </Button>
              </div>

              {/* Sidebar Content */}
              <div className="flex-1 min-h-0">
                <ConversationHistoryPanel />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
